
import React from 'react';
import { FlowRender } from "../flow-render";
import { RadioButton } from "../prm/radio-button";
import { Text, phoneValidator, dobValidator, ssnValidator, mmddyyyyValidator } from "../prm/text";
import { Name } from "../prm/name";
import { sprintf } from "sprintf-js";
import { GreyBox } from "../../client/funcs/utils";
import {is_custodial, is_ira, is_ira_rollover, is_transfer, is_ugma, role_titles} from "../onboarding-utils";
import { StateOptionMenu } from "../prm/state-menu";
import {OptionMenu} from "../prm/option-menu";
import {set_source_of_funds} from "./source-of-funds";
import {get_age} from "../time";
import {ProxyDocumentDelivery} from "../docusign-constants";
import {InvestmentAccountType} from "../proto/investment_accounts";

const VIMEO_BASE_ACCT = "952132444";
const VIMEO_BASE_CUSTODIAL = "964880430";
const VIMEO_NEW_ACCT = "964876527";

const open_text = 'OK time to open up your account';

const same_address = new RadioButton(
  new Name("same_address"),
  [
    new Name("same_address", "Same Physical Address as first")
  ]);
same_address.do_checkbox = true;

const same_phone = new RadioButton(
  new Name("same_phone"),
  [
    new Name("same_phone", "Same Phone number as first")
  ]);
same_phone.do_checkbox = true;

const same_email = new RadioButton(
  new Name("same_email"),
  [
    new Name("same_email", "Same Email as first")
  ]);
same_email.do_checkbox = true;

const dwolla_checkbox = new RadioButton(
  new Name("dwolla_checkbox"),
  [
    new Name("dwolla_tos_pp"),
  ]
);
dwolla_checkbox.do_checkbox = true

const ca_checkbox = new RadioButton(
  new Name("ca_checkbox"),
  [
    new Name("ca_tos_pp"),
  ]
);
ca_checkbox.do_checkbox = true;


const personal_text =
  "All your personal data will be kept secure and confidential.";

const prm_info = [
  {
    id: "first_name",
    label: "First Name",
  },
  {
    id: "last_name",
    label: "Last Name",
  },
  {
    id: "dob",
    label: "Date of Birth",
    fake: {
      path: "date.past",
      arg0: 40,
      arg1: 18,
    },
    validator: dobValidator,
    blurValidator: mmddyyyyValidator,
    hint: "MM/DD/YYYY",
  },
  {
    id: "ss",
    label: "Social Security",
    fake: {
      path: "finance.accountTemplate",
      arg0: "###-##-####",
    },
    validator: ssnValidator,
  },
  {
    id: "phone_number",
    label: "Phone Number",
    fake: {
      path: "phone.phoneNumber",
      arg0: "(###) ###-####",
    },
    validator: phoneValidator,
  },
  {
    id: "email",
    label: "Email Address",
  },
  {
    id: "street",
    label: "Street",
    fake: {
      path: "address.streetAddress",
    }
  },
  {
    id: "city",
    label: "City",
    fake: {
      path: "address.city",
    }
  },
  {
    id: "state",
    label: "State",
    fake: {
      path: "address.stateAbbr",
    }
  },
  {
    id: "zip",
    label: "Zip",
    fake: {
      path: "address.zipCode",
    }
  },
];

class PersonalInfo extends FlowRender {

  constructor(index) {
    super(index, [
      dwolla_checkbox,
      ca_checkbox,
      same_address,
      same_phone,
      same_email,
    ]);

    this.create_params("role_0");
    this.create_params("role_1");

    same_address.onChangeCallback = this.onCopyInfo(
      same_address, [ 'street', 'city', 'state', 'zip']);
    same_phone.onChangeCallback = this.onCopyInfo(same_phone, ['phone_number']);
    same_email.onChangeCallback = this.onCopyInfo(same_email, ['email']);
    this.url = "personal_info";
  }

  init(componentState, appState, component) {
    super.init(componentState, appState, component);
    if (!component.isBaseAcct()) {
      this.role_0.forEach(prm => {
        prm.setDisabled(componentState, true);
      })
    } else {
      // Just disable the email
      const email_prm = this.role_0.find(prm => prm.name.id === "role_0_email");
      if (email_prm) {
        email_prm.setDisabled(componentState, true);
      }

      const onboarding = component.getOnboardingData();
      if (onboarding.docusign_delivery_type === ProxyDocumentDelivery.NO_ENVELOPE) {
        const first_prm = this.role_0.find(prm => prm.name.id === "role_0_first_name");
        const last_prm = this.role_0.find(prm => prm.name.id === "role_0_last_name");
        first_prm.setDisabled(componentState, true);
        last_prm.setDisabled(componentState, true);
      }
    }
  };

  canNavigateBack = (component) => {
    const onboarding = component.getOnboardingData();
    if (onboarding && onboarding.first_page === "personal_info") {
      return false;
    }
    return true;
  }

  create_params = (prefix) => {

    const fake_first_name = {
      path: "name.firstName",
    };
    const fake_last_name = {
      path: "name.lastName",
    };
    const fake_email = {
      path: "internet.email",
    }

    this[prefix] = [];

    prm_info.forEach(({id, label, fake, validator, blurValidator, hint}) => {
      let local_fake = fake;
      if (prefix === "role_1") {
        if (id === 'first_name') {
          local_fake = fake_first_name;
        } else if (id === 'last_name') {
          local_fake = fake_last_name;
        } else if (id === 'email') {
          local_fake = fake_email;
        }
      }

      const new_id = prefix + "_" + id;
      let prm;

      if (id === "state") {
        const menu = new StateOptionMenu(new Name(new_id, label));
        menu.fake = 1;
        menu.onChangeCallback = this.onChange;
        prm = menu;
      } else {
        const text = new Text(
          new Name(new_id, label), null, local_fake);
        if (validator) {
          text.setValidator(validator);
        }
        if (blurValidator) {
          text.setBlurValidator(blurValidator);
        }
        if (hint) {
          text.hint = hint;
        }
        text.onAction = this.onTextEdit;
        prm = text;
      }

      this.prmList.push(prm);
      this[prefix].push(prm);
    });
  };

  setChildAgeError = (component, prm, curr_age, ugma_age, new_state) => {
    const highlight_rects = [ prm.getHighlightRect() ];

    Object.assign(new_state, { highlight_rects });
    new_state.role_1_curr_age = curr_age;
    new_state.role_1_ugma_age = ugma_age;
  }

  check_ugma_dob = (component, state) => {
    const onboarding = component.getOnboardingData();
    const _is_ugma = is_ugma(onboarding);
    if (!_is_ugma) {
      return {
        highlight_rects: [],
        role_1_curr_age: "",
        role_1_ugma_age: "",
      }
    }

    const ugma_age = parseInt(onboarding.child_age_menu, 10);
    const prm = this.prmList.find(prm => prm.name.id === "role_1_dob");
    const dob = prm.getValue(state);
    const curr_age = get_age(dob);

    if (curr_age >= ugma_age) {
      return {
        highlight_rects: [ prm.getHighlightRect() ],
        role_1_curr_age: curr_age,
        role_1_ugma_age: ugma_age,
      }
    }

    return {
      highlight_rects: [],
      role_1_curr_age: "",
      role_1_ugma_age: "",
    };
  }

  /** Called after a text entry is finished editing. */
  onTextEdit = (component, prm) => {
    //const onboarding = component.getOnboardingData();
    const newState = Object.assign({}, component.state);
    this.onChange(component, newState);

    if (prm.name.id === "role_1_dob") {
      const result = this.check_ugma_dob(component, newState);
      if (result) {
        Object.assign(newState, result);
      }
    }
    component.setState(newState);
  };

  onFinish = (component) => {

    if (component.state.dirty && component.isBaseAcct()) {
      // Start the Dwolla customer creation process.
      component.maybeCreateDwollaCustomer();
    }

    const onboarding = component.getOnboardingData();
    // Verify that we need to save the source-of-funds data.
    let new_onboarding_data;
    if (is_ira_rollover(onboarding)) {
      new_onboarding_data = set_source_of_funds("other", "Rollover");
    } else if (is_ira(onboarding)) {
      new_onboarding_data = set_source_of_funds("salary");
    }

    if (new_onboarding_data) {
      component.writeIfDirty(new_onboarding_data);
    }
  }

  onCopyInfo = (prm, prms_to_copy) => (component, newState) => {
    if (prm.isClicked(newState)) {
      this.doAddressCopy(newState, prms_to_copy);
    } else {
      this.doAddressEnable(newState, prms_to_copy);
    }
  };

  doAddressEnable = (newState, prms_to_copy) => {
    prms_to_copy.forEach(prm_id => {
      const prm = this.findPrm("role_1_" + prm_id);
      if (!prm) {
        return;
      }
      prm.setDisabled(newState, false);
    })
  };

  doAddressCopy = (newState, prms_to_copy) => {
    newState.dirty = true;

    prms_to_copy.forEach(prm => {
      const from_id = "role_0_" + prm;
      const to_id = "role_1_" + prm;
      const from_prm = this.findPrm(from_id);
      const to_prm = this.findPrm(to_id);
      if (!from_prm || !to_prm) {
        // Iteration break
        return;
      }
      if (to_prm.type === Text.type) {
        to_prm.setValue(newState, from_prm.getValue(newState));
      } else if (to_prm.type === OptionMenu.type) {
        to_prm.setValue(newState, from_prm.getValue(newState).id);
      }
      to_prm.setDisabled(newState, true);
    });
  }

  canNavigateNext(component) {
    const num_roles = role_titles(component.getOnboardingData()).length;
    let all_prm = [];
    if (num_roles === 1) {
      all_prm = [...this.role_0];
    } else if (num_roles === 2) {
      all_prm = this.role_0.concat(this.role_1);
    }
    all_prm = all_prm.filter(prm => !prm.name.id.endsWith("_state"));

    if (component.isBaseAcct()) {
      all_prm.push(dwolla_checkbox);
      all_prm.push(ca_checkbox);
    }

    const highlight_rects = [];
    all_prm.forEach(prm => prm.isFilledElseHighlight(component.state, highlight_rects));

    if (highlight_rects.length > 0) {
      component.setState({ highlight_rects });
      return null;
    }

    const result = this.check_ugma_dob(component, component.state);
    if (result.highlight_rects.length > 0) {
      component.setState(result);
      return null;
    }

    const onboarding = component.getOnboardingData();
    if (is_ira_rollover(onboarding) || is_ira(onboarding) || is_transfer(onboarding)
      || onboarding.account_type === InvestmentAccountType.custodial_ira.id) {
      return component.getRootUrl() + "/employment";
    }
    return component.getRootUrl() + "/source_of_funds";

  };

  calcVimeoId = component => {
    const onboarding = component.getOnboardingData();
    if (component.isBaseAcct()) {
      if (is_custodial(onboarding)) {
        return VIMEO_BASE_CUSTODIAL;
      }
      return VIMEO_BASE_ACCT;
    }
    return VIMEO_NEW_ACCT;
  }

  renderColumn = (component, index, role) => {
    const prm_group = sprintf("role_%d", index);
    const prm_list = this[prm_group];
    const span_style = {
      marginRight: "1rem",
    };
    const address_text = "Physical Address";
    const is_first = index === 0;
    const all_prms = prm_list.map(prm => {
      const container_style = {
        marginTop: "1rem",
      };
      const label_style = {
        paddingLeft: "1rem",
      };
      const prefix_elems = {
        street: [
          (<span style={span_style}>{address_text}</span>),
          same_address.renderCheckboxLabel(0, component, {}, label_style),
        ],
        phone_number: [
          (<span style={span_style}>{' '}</span>),
          same_phone.renderCheckboxLabel(0, component, {}, label_style),
        ],
        email: [
          (<span style={span_style}>{' '}</span>),
          same_email.renderCheckboxLabel(0, component, {}, label_style),
        ],
      };
      let prefix = null;
      const prefix_index = is_first ? 0 : 1;
      Object.keys(prefix_elems).forEach(prop => {
        if (prm.name.id.endsWith(prop)) {
          prefix = prefix_elems[prop];
        }
      });
      const input_style = {
        width: "100%",
      }

      let prm_div;
      if (prm instanceof Text) {
        prm_div = prm.renderInputDiv(component, input_style, prm.name.label);
      } else if (prm instanceof OptionMenu) {
        prm_div = prm.renderMenuDiv(component, input_style, prm.name.label);
      }
      const is_dob = prm.name.id.endsWith("_dob");
      let err_elem;
      if (is_dob && component.state.role_1_curr_age && component.state.role_1_ugma_age) {
        const text = sprintf(
          "Child is %d and too old. This account transfers to the child at %d.",
          component.state.role_1_curr_age,
          component.state.role_1_ugma_age);
        const style = {
          color: "red",
          paddingTop: "0.5rem",
        }
        if (index === 0) {
          style.visibility = "hidden";
        }
        err_elem = (
          <div style={style}>
            { text }
          </div>
        )
      }
      return (
        <div key={prm.name.id} align={"left"} style={container_style}>
          { prefix && prefix[prefix_index] }
          { prm_div }
          { err_elem }
        </div>
      );
    });
    const column_style = {
      marginTop: "2rem",
    };
    return (
      <div style={column_style}>
        { role && <div align={"center"}>{role}</div> }
        <GreyBox>
          { all_prms }
        </GreyBox>
      </div>
    )
  };

  renderHeader = (component, num_roles) => {
    const text = sprintf(personal_text, num_roles === 2 ? "s" : "");
    const link = (url, text) => (<a href={url} target="_blank" rel="noopener noreferrer">{text}</a>);

    const vimeo_id = this.calcVimeoId(component);
    const ca_tac_url = "https://www.cleveralpha.com/terms-and-conditions";
    const ca_pp_url = "https://www.cleveralpha.com/privacy-policy";
    const text_1 = "By checking this box, you agree to CleverAlpha's ";
    const ca_text = (
      <span>
        {text_1}
        { link(ca_tac_url, "Terms and Conditions")}
        { ' and ' }
        { link(ca_pp_url, "Privacy Policy")}
      </span>
    );
    const dwolla_tos = "https://www.dwolla.com/legal/dwolla-account-terms-of-service";
    const dwolla_text = (
      <span>
        { "By checking this box, you agree to Dwolla's " }
        { link (dwolla_tos, "Terms of Service") }
      </span>
    )

    const TOS = (
      <div>
        <div style={{display: "flex", alignItems: "center", paddingTop: "2rem"}}>
          {/* {ca_checkbox.renderCheckbox(0, component)}
          <div style={{display: "inline-block", width: "80%", marginLeft: "2rem"}}>
            {ca_text}
          </div> */}
          {ca_checkbox.renderCheckboxWithInfo(0, component, ca_text)}
        </div>
        <div style={{display: "flex", alignItems: "center", paddingTop: "1rem"}}>
          {/* {dwolla_checkbox.renderCheckbox(0, component)}
          <div style={{display: "inline-block", width: "80%", marginLeft: "2rem"}}>
            {dwolla_text}
          </div> */}
          {dwolla_checkbox.renderCheckboxWithInfo(0, component, dwolla_text)}
        </div>

      </div>
    );

    return (
      <div>
        {this.renderTitle(component, open_text, "50%", vimeo_id)}
        {this.renderProgress(component)}
        {this.renderSingleColumn((
          <div>
            <div>
              <div style={{ fontSize: "1.5rem"}}>{text}</div>
            </div>
            { component.isBaseAcct() && TOS }
          </div>
        ), "50%") }
      </div>
    )
  };

  render = component => {
    const titles = role_titles(component.getOnboardingData());

    const columns = titles.map((title, index) => {
      return (
        <div style={{ width: "30%"}} key={index}>
          { this.renderColumn(component, index, title) }
        </div>
      )
    });
    if (columns.length === 2) {
      columns.splice(1, 0, (<div key={10} style={{width: "2rem"}}/>));
    }

    const section_style = {
      paddingTop: "0",
      paddingBottom: "0",
    }
    return (
      <div>
        { this.renderHighlightRects(component) }
        { this.renderHeader(component, titles.length) }
        <section className="section" style={section_style}>
          <div style={{display: "flex", margin: "auto"}}>
            <div style={{flex: "1"}}/>
            {columns}
            <div style={{flex: "1"}}/>
          </div>
        </section>
        { this.renderNextButtonRow(component, this.canNavigateBack(component)) }
      </div>
    )
  }
}

export { PersonalInfo };
